import React, { ReactNode, useState, useCallback, useContext, useEffect } from "react";
import { ImpersonationContext } from "../../contexts/ImpersonationContext";
import { 
  ImpersonationRequest, 
  startImpersonation as apiStartImpersonation,
  stopImpersonation as apiStopImpersonation 
} from "../../functions/requests/impersonation";
import { OidcConfigurationNameContext } from "../../contexts/OidcConfigurationNameContext";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import { UserInfoContext } from "../../contexts/UserInfoContext";

export const ImpersonationProvider = ({ children }: { children: ReactNode }) => {
  const { oidcConfigurationName } = useContext(OidcConfigurationNameContext);
  const oidcAccessToken = useOidcAccessToken(oidcConfigurationName);
  const { reload: reloadUserInfo } = useContext(UserInfoContext);

  const [currentImpersonation, setCurrentImpersonation] = useState<ImpersonationRequest | null>(null);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const startImpersonation = useCallback(async (request: ImpersonationRequest) => {
    if (!oidcAccessToken?.accessToken) {
      throw new Error("No access token available");
    }

    setLoading(true);
    setError(null);

    try {
      await apiStartImpersonation(oidcAccessToken, request);
      setCurrentImpersonation(request);
      setIsImpersonating(true);
      
      // Reload user info to get impersonated user details
      await reloadUserInfo();
      
      console.log(`Started impersonating: ${request.target_user_id}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start impersonation';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [oidcAccessToken, reloadUserInfo]);

  const stopImpersonation = useCallback(async () => {
    if (!oidcAccessToken?.accessToken) {
      throw new Error("No access token available");
    }

    setLoading(true);
    setError(null);

    try {
      await apiStopImpersonation(oidcAccessToken);
      setCurrentImpersonation(null);
      setIsImpersonating(false);
      
      // Reload user info to get original user details
      await reloadUserInfo();
      
      console.log('Stopped impersonation');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to stop impersonation';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [oidcAccessToken, reloadUserInfo]);

  const value = {
    currentImpersonation,
    isImpersonating,
    startImpersonation,
    stopImpersonation,
    loading,
    error,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}; 